import { observer } from 'mobx-react';
import * as React from 'react';
import { Route, Switch, Redirect, RouteProps } from 'react-router-dom';
import Loadable from 'react-loadable';

import { lazyInject, rootContainer } from './ioc';
import { ClientDataStore } from './core/stores/client-data.store';
import { BusyLoader } from './core/components/busy-loader';
import { ErrorPage } from './core/components/error-page/error-page';
import { HomePageContainer } from './core/components/home-page/home-page';


const clientData = rootContainer.get(ClientDataStore);

const LoadableLogin = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "login" */
            './login/components/login-routes'
        ).then((module) => module.LoginRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableWebScheduler = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "schedule" */
            './web-scheduler/components/web-scheduler-routes'
        ).then((module) => module.WebSchedulerRoutes) as Promise<React.SFC>,
    loading: BusyLoader,
});

const LoadableInvoice = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "invoice" */
            './invoice/components/invoice-routes'
        ).then((module) => module.InvoiceRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableEstimate = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "estimate" */
            './estimate/components/estimate-routes'
        ).then((module) => module.EstimateRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableSignature = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "signature" */
            './signature/components/signature-routes'
        ).then((module) => module.SignatureRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableStatement = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "statement" */
            './statement/components/statement-routes'
        ).then((module) => module.StatementRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableMarketing = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "marketing" */
            './marketing/components/marketing-routes'
        ).then((module) => module.MarketingRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableHistory = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "history" */
            './history/components/history-routes'
        ).then((module) => module.HistoryRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadablePhoneCapture = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "phonecapture" */
            './phone-capture/components/routes'
        ).then((module) => module.PhoneCaptureRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableSavedPaymentMethod = Loadable({
    loader: () => import(
        './payment-method/components/payment-method-route'
    ).then(module => module.PaymentMethodRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});

const LoadableMembership = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "memberships" */
            './memberships/components/memberships-routes'
        ).then((module) => module.MembershipRoutes) as Promise<React.FC>,
    loading: BusyLoader,
});
type PrivateProps = RouteProps & {
    allowAnonPayments?: boolean;
    allowAnonEstimates?: boolean;
    allowAnonStatements?: boolean;
    allowAnonVisitAction?: boolean;
    allowAnonUnsubscribe?: boolean;
    allowAnonPhoneCapture?: boolean;
};

export const PrivateRoute: React.SFC<PrivateProps> = ({
    component: Component,
    allowAnonPayments,
    allowAnonEstimates,
    allowAnonStatements,
    allowAnonVisitAction,
    allowAnonUnsubscribe,
    allowAnonPhoneCapture,
    ...rest
}) => {
    if (!Component) {
        return null;
    }

    return (
        <Route
            {...rest}
            render={(props) =>
                clientData.isLoggedIn ||
                    (allowAnonPayments && clientData.isAnonymousPayments) ||
                    (allowAnonEstimates && clientData.isAnonymousEstimates) ||
                    (allowAnonStatements && clientData.isAnonymousStatement) ||
                    (allowAnonVisitAction && clientData.isAnonymousVisitActionRequests) ||
                    (allowAnonUnsubscribe && clientData.isAnonymousUnsubscribe) ||
                    (allowAnonPhoneCapture && clientData.isAnonymousPhoneCapture) ? (
                        <Component {...props} />
                    ) : (
                        <Redirect
                            to={{
                                pathname: getUnauthorizedLink(),
                                state: { from: props.location },
                            }}
                        />
                    )
            }
        />
    );
};

const getUnauthorizedLink = () => {
    if (clientData.isAnonymousPayments) {
        return '/invoice/expired-link';
    }
    if (clientData.isAnonymousEstimates) {
        return '/estimate/expired-link';
    }
    if (clientData.isAnonymousUnsubscribe) {
        return '/marketing/expired-link';
    }
    return '/login/welcome';
};

const LoginRoute: React.SFC<RouteProps> = ({ component: Component, ...rest }) => {
    if (!Component) {
        return null;
    }

    return (
        <Route
            {...rest}
            render={(props) =>
                !clientData.isLoggedIn && !clientData.isAnonymousPayments ? (
                    <Component {...props} />
                ) : (
                        <Redirect to={{ pathname: '/' }} />
                    )
            }
        />
    );
};

@observer
export class AppRoutes extends React.Component {
    @lazyInject(ClientDataStore) private clientData!: ClientDataStore;

    render() {
        if (this.clientData.initError === 'EstimateDeleted') {
            return (
                <Switch>
                    <Route component={() => <ErrorPage type="estimate-no-longer-exits" />} />
                </Switch>
            );
        } else if (this.clientData.initError === 'Generic') {
            return (
                <Switch>
                    <Route component={() => <ErrorPage type="system-down" />} />
                </Switch>
            );
        }

        return (
            <Switch>
                <Route path="/system-down" component={() => <ErrorPage type="system-down" />} />
                <LoginRoute path="/login" component={LoadableLogin} />
                <LoginRoute path="/booking" component={LoadableLogin} />
                <Route path="/invoice" component={LoadableInvoice} />
                <Route path="/estimate" component={LoadableEstimate} />
                <Route path="/signatures" component={LoadableSignature} />
                <Route path="/statement" component={LoadableStatement} />
                <Route path="/statements" component={LoadableStatement} />
                <Route path="/marketing" component={LoadableMarketing} />
                <Route path="/memberships" component={LoadableMembership} />
                <Route path="/contracts" component={LoadableMembership} />
                <PrivateRoute path="/history" component={LoadableHistory} />
                <PrivateRoute path="/schedule" component={LoadableWebScheduler} />
                <PrivateRoute path="/home" component={HomePageContainer} />
                <Redirect exact path="/" to={this.clientData.loginData.defaultUrl || '/home'} />
                <Route path="/phonecapture" component={LoadablePhoneCapture} />
                <PrivateRoute path="/savedpaymentmethod" component={LoadableSavedPaymentMethod} />
                {}
                <Route component={() => <ErrorPage type="not-found" />} />
            </Switch>
        );
    }
}
